/**
 * Discounts that are a percentage OR a fixed amount, applied in a stated order (B-0132). Pure; synced to the API
 * (/v1/converters/discount). The percent-only maths in invest.ts (`discount`) is unchanged and a test holds this file to it.
 *
 * Each discount is applied to the price left by the one before it: 100, 20% then $15 off = 80 - 15 = 65; $15 off then 20% = 85 x 0.8 = 68.
 * A discount that would take the price below 0 is refused (a coupon cannot pay you); exactly 0 is allowed. Tax is added on the discounted price.
 */
import type { CalcStep } from "@/lib/calc/steps";

export type DiscountKind = "percent" | "amount";
export interface DiscountItem { kind: DiscountKind; value: number }
export interface DiscountStep { kind: DiscountKind; value: number; before: number; off: number; after: number }
export type StackOutcome =
  | { ok: true; original: number; steps: DiscountStep[]; final: number; saved: number; effectivePct: number; tax: number; total: number }
  | { ok: false; message: string };

export function stackDiscounts(original: number, items: DiscountItem[], taxPct = 0): StackOutcome {
  if (!Number.isFinite(original) || original <= 0) return { ok: false, message: "Enter an original price greater than 0." };
  if (!Number.isFinite(taxPct) || taxPct < 0) return { ok: false, message: "Tax cannot be negative." };
  let price = original;
  const steps: DiscountStep[] = [];
  for (const it of items) {
    if (!Number.isFinite(it.value)) return { ok: false, message: "Enter valid numbers." };
    if (it.kind === "percent" && (it.value < 0 || it.value > 100)) return { ok: false, message: "A discount must be between 0% and 100%." };
    if (it.kind === "amount" && it.value < 0) return { ok: false, message: "A fixed amount off cannot be negative." };
    const off = it.kind === "percent" ? (price * it.value) / 100 : it.value;
    // Tolerance so $85 off $85 (floating point dust) is a free item, not an error.
    if (off > price + 1e-9) return { ok: false, message: `${describeItem(it)} is more than the ${money(price)} left at that point, so the price would go below 0. Lower it or apply it in another order.` };
    const after = Math.max(0, price - off);
    steps.push({ kind: it.kind, value: it.value, before: price, off, after });
    price = after;
  }
  const tax = (price * taxPct) / 100;
  return { ok: true, original, steps, final: price, saved: original - price, effectivePct: ((original - price) / original) * 100, tax, total: price + tax };
}

const money = (n: number) => n.toFixed(2);
const num = (n: number) => String(Math.round(n * 1e6) / 1e6);
export function describeItem(it: DiscountItem): string { return it.kind === "percent" ? `${num(it.value)}% off` : `${num(it.value)} off (fixed amount)`; }

/** "20% off, then 15 off (fixed amount)": the order in words. */
export function orderText(items: DiscountItem[]): string { return items.map(describeItem).join(", then "); }

/** The working. `f` formats money. */
export function discountSteps(r: Extract<StackOutcome, { ok: true }>, taxPct: number, f: (n: number) => number | string = (n) => Math.round((n + Number.EPSILON) * 100) / 100): CalcStep[] {
  const steps: CalcStep[] = r.steps.map((s, i): CalcStep => s.kind === "percent"
    ? { label: `Discount ${i + 1}: ${num(s.value)}% off`, formula: "Price x (1 - discount / 100)", inputs: { price: f(s.before), "discount %": s.value }, result: f(s.after) }
    : { label: `Discount ${i + 1}: ${num(s.value)} off`, formula: "Price - fixed amount", inputs: { price: f(s.before), "amount off": f(s.value) }, result: f(s.after) });
  steps.push({ label: "Total saved", formula: "Original price - final price", inputs: { original: f(r.original), final: f(r.final) }, result: `${f(r.saved)} (${Math.round(r.effectivePct * 100) / 100}% off)` });
  if (taxPct > 0) steps.push({ label: "Tax on the discounted price", formula: "Final price x tax %", inputs: { "final price": f(r.final), "tax %": taxPct }, result: f(r.tax) }, { label: "Total with tax", formula: "Final price + tax", inputs: { "final price": f(r.final), tax: f(r.tax) }, result: f(r.total) });
  return steps;
}

/** Find mode: the discount from an original and a sale price, as a percent and as a fixed amount off. */
export function fixedAmountText(original: number, sale: number, fmt: (n: number) => string): string | null {
  if (!(original > 0) || !Number.isFinite(sale) || sale < 0) return null;
  const saved = original - sale;
  if (saved === 0) return "No discount: the sale price equals the original price.";
  const pct = Math.round((saved / original) * 1e4) / 100;
  return saved > 0
    ? `${pct}% off is the same as a fixed ${fmt(saved)} coupon on this ${fmt(original)} price.`
    : `The sale price is ${fmt(-saved)} (${-pct}%) above the original, so this is a price increase, not a discount.`;
}
